import React, { useState, useEffect, useRef } from 'react';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import CircularProgress from '@mui/material/CircularProgress';
import Avatar from '@mui/material/Avatar';
import Divider from '@mui/material/Divider';
import { useAuth } from '@/context/AuthContext';
import { useCredits } from '@/context/CreditsContext';
import { useI18n } from '@/i18n/I18nContext';
import { AIService } from '@/services/AIService';
import { AIInsight, AIQuestion } from '@/types';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import SendIcon from '@mui/icons-material/Send';
import PsychologyIcon from '@mui/icons-material/Psychology';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import Chip from '@mui/material/Chip';
import CreditsDisplay from '@/components/CreditsDisplay';

type Message = {
	id: string;
	role: 'user' | 'assistant';
	content: string;
	timestamp: Date;
};

const AIAssistantPage: React.FC = () => {
	const { user, getAuthHeader, getUserId } = useAuth();
	const { setCreditsLoading, loadCredits } = useCredits();
	const { t, language } = useI18n();
	const [question, setQuestion] = useState('');
	const [loading, setLoading] = useState(false);
	const [insights, setInsights] = useState<AIInsight[]>([]);
	const [questions, setQuestions] = useState<AIQuestion[]>([]);
	const [messages, setMessages] = useState<Message[]>([]);
	const messagesEndRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (user) {
			loadInsights();
			loadQuestionHistory();
		}
	}, [user]);

	useEffect(() => {
		messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
	}, [messages]);

	const loadInsights = async () => {
		try {
			const userId = getUserId();
			if (!userId) return;
			const authHeader = getAuthHeader();
			const response = await AIService.getInsights(userId, undefined, undefined, authHeader);
			setInsights(response.items || []);
		} catch (error) {
			console.error('Error loading insights:', error);
		}
	};

	const loadQuestionHistory = async () => {
		try {
			const userId = getUserId();
			if (!userId) return;
			const authHeader = getAuthHeader();
			const response = await AIService.getQuestionHistory(userId, authHeader);
			setQuestions(response.items || []);
		} catch (error) {
			console.error('Error loading question history:', error);
		}
	};

	const handleAskQuestion = async () => {
		if (!question.trim() || loading) return;

		const userMessage: Message = {
			id: `msg-${Date.now()}`,
			role: 'user',
			content: question,
			timestamp: new Date()
		};

		setMessages(prev => [...prev, userMessage]);
		const currentInput = question;
		setQuestion('');
		setLoading(true);
		setCreditsLoading(true);

		try {
			const userId = getUserId();
			if (!userId) {
				throw new Error('User ID not available. Please log in again.');
			}
			const authHeader = getAuthHeader();
			const response = await AIService.askQuestion(userId, currentInput, undefined, undefined, authHeader);
			console.log('AI Response received:', response);
			
			// Extract answer from response - handle different possible structures
			let answer: string | undefined;
			
			if (response && typeof response === 'object') {
				// Try direct access first (expected structure)
				answer = (response as any).answer;
				
				// If not found, check nested structures
				if (!answer) {
					answer = (response as any).body?.answer 
						|| (response as any).data?.answer
						|| (response as any).result?.answer;
				}
			}
			
			if (!answer || typeof answer !== 'string') {
				console.error('No valid answer found in response:', response);
				throw new Error(`Invalid response format: ${JSON.stringify(response)}`);
			}
			
			const assistantMessage: Message = {
				id: `msg-${Date.now() + 1}`,
				role: 'assistant',
				content: answer,
				timestamp: new Date()
			};
			setMessages(prev => [...prev, assistantMessage]);
			toast.success(t.aiAssistant.answerReceived);
			await loadQuestionHistory();
		} catch (error: any) {
			console.error('Error asking question:', error);
			const errorMessage: Message = {
				id: `msg-${Date.now() + 1}`,
				role: 'assistant',
				content: `Error: ${error.message || 'Failed to get response'}`,
				timestamp: new Date()
			};
			setMessages(prev => [...prev, errorMessage]);
			toast.error(t.aiAssistant.errorAsking);
		} finally {
			setLoading(false);
			try {
				await new Promise((r) => setTimeout(r, 400));
				await loadCredits();
			} finally {
				setCreditsLoading(false);
			}
		}
	};

	return (
		<Container maxWidth="lg" sx={{ py: 4 }}>
			<ToastContainer position="top-right" />
			<Box sx={{ mb: 4 }}>
				<Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
					<Stack direction="row" spacing={2} alignItems="center">
						<PsychologyIcon color="primary" sx={{ fontSize: 40 }} />
						<Typography variant="h4" fontWeight={800}>
							{t.aiAssistant.title}
						</Typography>
					</Stack>
					<CreditsDisplay variant="chip" size="medium" showRefresh />
				</Stack>
				<Typography variant="body1" color="text.secondary">
					{t.aiAssistant.description}
				</Typography>
			</Box>

			<Grid container spacing={3}>
				<Grid item xs={12} md={8}>
					<Paper sx={{ p: 3, display: 'flex', flexDirection: 'column', height: 'calc(100vh - 200px)', minHeight: 600 }}>
						<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
							<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
								<PsychologyIcon color="primary" />
								<Typography variant="h6" fontWeight={700}>
									{t.aiAssistant.title}
								</Typography>
							</Box>
							<CreditsDisplay variant="text" size="small" />
						</Box>
						<Divider sx={{ mb: 2 }} />
						<Box
							sx={{
								flex: 1,
								overflowY: 'auto',
								p: 2,
								bgcolor: 'background.default',
								borderRadius: 2,
								display: 'flex',
								flexDirection: 'column',
								gap: 2,
								mb: 2
							}}
						>
							{messages.length === 0 && (
								<Box sx={{ textAlign: 'center', py: 4 }}>
									<Typography variant="body2" color="text.secondary">
										{language === 'pt' 
											? 'Pergunte-me qualquer coisa sobre homebrewing! Posso ajudá-lo com receitas, fermentação, técnicas de fabricação de cerveja e muito mais.'
											: 'Ask me anything about homebrewing! I can help you with recipes, fermentation, brewing techniques, and more.'}
									</Typography>
								</Box>
							)}
							{messages.map((message) => (
								<Box
									key={message.id}
									sx={{
										display: 'flex',
										gap: 1,
										justifyContent: message.role === 'user' ? 'flex-end' : 'flex-start',
										alignItems: 'flex-start'
									}}
								>
									{message.role === 'assistant' && (
										<Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
											<PsychologyIcon fontSize="small" />
										</Avatar>
									)}
									<Paper
										variant="outlined"
										sx={{
											p: 2,
											maxWidth: '70%',
											bgcolor: message.role === 'user' ? 'primary.main' : 'background.paper',
											color: message.role === 'user' ? 'primary.contrastText' : 'text.primary'
										}}
									>
										<Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
											{message.content}
										</Typography>
									</Paper>
									{message.role === 'user' && (
										<Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main' }}>
											{user?.email?.charAt(0).toUpperCase() || 'U'}
										</Avatar>
									)}
								</Box>
							))}
							{loading && (
								<Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
									<Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
										<PsychologyIcon fontSize="small" />
									</Avatar>
									<Paper variant="outlined" sx={{ p: 2 }}>
										<CircularProgress size={20} />
									</Paper>
								</Box>
							)}
							<div ref={messagesEndRef} />
						</Box>
						<Stack direction="row" spacing={1}>
							<TextField
								fullWidth
								placeholder={language === 'pt' ? 'Pergunte sobre homebrewing...' : 'Ask about homebrewing...'}
								value={question}
								onChange={(e) => setQuestion(e.target.value)}
								onKeyPress={(e) => {
									if (e.key === 'Enter' && !e.shiftKey) {
										e.preventDefault();
										handleAskQuestion();
									}
								}}
								disabled={loading}
								multiline
								maxRows={3}
							/>
							<Button
								variant="contained"
								onClick={handleAskQuestion}
								disabled={loading || !question.trim()}
								sx={{ minWidth: 100 }}
							>
								{loading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
							</Button>
						</Stack>
					</Paper>

					{insights.length > 0 && (
						<Paper sx={{ p: 3 }}>
							<Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
								<AutoAwesomeIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
								{t.aiAssistant.recentInsights}
							</Typography>
							<Stack spacing={2}>
								{insights.map((insight) => (
									<Box key={insight.id} sx={{ p: 2, bgcolor: 'background.default', borderRadius: 2 }}>
										<Stack direction="row" spacing={1} sx={{ mb: 1 }}>
											<Chip label={t.aiAssistant.insightTypes[insight.type as keyof typeof t.aiAssistant.insightTypes]} size="small" color="primary" />
											<Typography variant="subtitle2" fontWeight={700}>
												{insight.title}
											</Typography>
										</Stack>
										<Typography variant="body2" color="text.secondary">
											{insight.message}
										</Typography>
									</Box>
								))}
							</Stack>
						</Paper>
					)}
				</Grid>

				<Grid item xs={12} md={4}>
					<Paper sx={{ p: 3 }}>
						<Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
							{t.aiAssistant.questionHistory}
						</Typography>
						{questions.length === 0 ? (
							<Typography variant="body2" color="text.secondary">
								{t.aiAssistant.noQuestions}
							</Typography>
						) : (
							<Stack spacing={2}>
								{questions.slice(0, 5).map((q) => (
									<Box key={q.id} sx={{ p: 1.5, bgcolor: 'background.default', borderRadius: 1 }}>
										<Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
											{q.question}
										</Typography>
										{q.answer && (
											<Typography variant="caption" color="text.secondary">
												{q.answer.substring(0, 100)}...
											</Typography>
										)}
									</Box>
								))}
							</Stack>
						)}
					</Paper>
				</Grid>
			</Grid>
		</Container>
	);
};

export default AIAssistantPage;
